import { FC, PropsWithChildren, Ref, useCallback, useState } from 'react'
import { FieldError } from 'react-hook-form'

import { AddressSource } from '@forto-dev/customer-master-data-models'
import { LocationSearchResult } from '@forto-dev/locations-models'

import { AddressSelectorContext, AddressSelectorContextState } from './useAddressSelectorContext'
import {
  AddressSelectorDispatchContext,
  AddressSelectorDispatchContextState,
} from './useAddressSelectorDispatchContext'
import { AddressFormData } from '../schema'

export type AddressSelectorContextProviderProps = PropsWithChildren<{
  label?: string
  required?: boolean
  inputRef?: Ref<unknown>
  error?: FieldError
  disabled?: boolean
  value?: AddressFormData
  warning?: string
  onChange?: (selectedAddress: AddressFormData | null) => void
}>

export const AddressSelectorContextProvider: FC<AddressSelectorContextProviderProps> = (props) => {
  const { children, label, required, inputRef, disabled, value: valueFromProp, error, warning, onChange } = props

  const [addressFromSearch, setAddressFromSearch] = useState<LocationSearchResult | null>(null)
  const [addressDetail, setAddressDetail] = useState<AddressFormData | null>(null)

  const value = valueFromProp ?? addressDetail

  const [manualMode, setManualMode] = useState(value?.address?.source === AddressSource.MANUAL)

  const onAddressSearchChange = (selectedAddress: LocationSearchResult | null) => {
    setAddressFromSearch(selectedAddress)
    if (selectedAddress) {
      setManualMode(false)
    } else {
      onChange?.(null)
      setAddressDetail(null)
    }
  }

  const onAddManualAddress = () => {
    setManualMode(true)
    setAddressFromSearch(null)
    onChange?.({
      address: {
        source: AddressSource.MANUAL,
      },
      localAddress: null,
      addressFormatted: null,
      localAddressFormatted: null,
    })
  }

  const onSearchAddressResultSelected = useCallback(
    (selectedAddress: AddressFormData | null) => {
      if (selectedAddress) {
        onChange?.(selectedAddress)
        setAddressDetail(selectedAddress)
      }
    },
    [onChange]
  )

  const onManualAddressSelected = useCallback(
    (selectedAddress: AddressFormData | null) => {
      if (selectedAddress) {
        onChange?.(selectedAddress)
      }
    },
    [onChange]
  )

  const state: AddressSelectorContextState = {
    addressFromSearch,
    manualMode,
    label,
    required,
    inputRef,
    disabled,
    value,
    error,
    warning,
  }

  const dispatchState: AddressSelectorDispatchContextState = {
    onAddressSearchChange,
    onAddManualAddress,
    onSearchAddressResultSelected,
    onManualAddressSelected,
  }

  return (
    <AddressSelectorContext.Provider value={state}>
      <AddressSelectorDispatchContext.Provider value={dispatchState}>
        {children}
      </AddressSelectorDispatchContext.Provider>
    </AddressSelectorContext.Provider>
  )
}
